import { isDraft } from 'immer';
import { t } from '../i18n';

/**
 * 不可变性工具。
 *
 * ## 为什么要主动做这件事
 *
 * `reduce` 的产物由 immer 的 `autoFreeze` 冻结，但**手写构造的状态**
 * （`createInitialState` / `createLobbyState`）和 **`project` 的产物**不会。
 * 于是"状态是否可变"在不同阶段是不一样的 —— 这正是那种
 * **平时没事、偶发一次极难查**的隐晦 bug 的温床。
 *
 * 所以定一条统一规矩：
 *
 * > **`core/` 交出去的一切都是深度冻结的。**
 *
 * 于是任何原地修改都会**立刻抛异常**（ESM 一律是严格模式），
 * 而不是悄悄改坏一份大家都以为不可变的状态。
 *
 * ## 为什么不等生产环境关掉
 *
 * immer 的文档建议在生产环境 `setAutoFreeze(false)` 换性能。
 * 这里**不关**：这是个回合制卡牌游戏，一局最多的操作也就几十次，
 * 冻结 56 张牌的开销完全可以忽略，而换来的是一条无条件成立的保证。
 */

/** 深度冻结。返回原对象，方便 `return deepFreeze({...})` 这样用 */
export function deepFreeze<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (value === null || typeof value !== 'object') return value;

  const object = value as object;

  // 冻结一个 immer draft 是没意义的（它只是代理），而且遍历它会撞上
  // "ownKeys trap result did not include 'type_'" 这种看不懂的报错。
  // 与其让人对着这句话查半天，不如在这里说清楚该怎么办。
  if (isDraft(object)) {
    throw new TypeError(
      t('core.immutable.draftReceived'),
    );
  }

  if (seen.has(object)) return value; // 防环
  seen.add(object);

  Object.freeze(object);

  // 数组的索引和对象的可枚举属性都要走到。`length` 是数字，会在下一次递归里直接返回
  for (const key of Object.getOwnPropertyNames(object)) {
    deepFreeze((object as Record<string, unknown>)[key], seen);
  }

  return value;
}

/**
 * 找出结构里**没有被冻结**的地方，返回它们的路径。
 *
 * 空数组 = 全都冻上了。测试和开发工具用它当裁判 ——
 * 光"调用了 deepFreeze"不算数，得**验出来**才算。
 */
export function findUnfrozen(value: unknown, path = '$', seen: WeakSet<object> = new WeakSet()): string[] {
  if (value === null || typeof value !== 'object') return [];

  const object = value as object;
  if (seen.has(object)) return [];
  seen.add(object);

  const problems: string[] = [];
  if (!Object.isFrozen(object)) problems.push(path);

  for (const key of Object.getOwnPropertyNames(object)) {
    problems.push(...findUnfrozen((object as Record<string, unknown>)[key], `${path}.${key}`, seen));
  }

  return problems;
}
